import { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';

const StudentRequestList = ({ 
  requests, 
  onApprove, 
  onReject, 
  onToggleStatus,
  pagination, 
  onPageChange, 
  currentFilter 
}) => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'approve', 'reject', 'details', or 'toggle-status'
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [statusAction, setStatusAction] = useState(true); // true for activate, false for deactivate
  const [processing, setProcessing] = useState(false);

  // Safety check for undefined or null requests and pagination
  const safeRequests = requests || [];
  const safePagination = pagination || { current: 1, total: 0, count: 0, totalRecords: 0 };

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setModalType('approve');
    setAdminNotes('');
    setShowModal(true);
  };

  const handleRejectClick = (request) => {
    setSelectedRequest(request);
    setModalType('reject');
    setRejectionReason('');
    setAdminNotes('');
    setShowModal(true);
  };

  const handleToggleStatusClick = (request, newStatus) => {
    setSelectedRequest(request);
    setModalType('toggle-status');
    setStatusAction(newStatus);
    setAdminNotes('');
    setShowModal(true);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setModalType('details');
    setShowModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedRequest) return;

    setProcessing(true);
    try {
      if (modalType === 'approve') {
        await onApprove(selectedRequest._id, adminNotes);
      } else if (modalType === 'reject') {
        if (!rejectionReason.trim()) {
          alert('Please provide a rejection reason');
          return;
        }
        await onReject(selectedRequest._id, rejectionReason, adminNotes);
      } else if (modalType === 'toggle-status') {
        await onToggleStatus(selectedRequest._id, statusAction, adminNotes);
      }
      setShowModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error processing request:', error);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (request) => {
    if (request.isVerified) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Approved</span>;
    } else if (request.rejectionReason) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Rejected</span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>;
    }
  };

  const getActivityBadge = (request) => {
    if (request.isActive) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Active</span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Inactive</span>;
    }
  };

  const getImageSrc = (imageData) => {
    if (!imageData) return null;
    if (imageData.startsWith('http')) return imageData;
    return `data:image/jpeg;base64,${imageData}`;
  };

  if (safeRequests.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No {currentFilter} students</h3>
        <p className="mt-1 text-sm text-gray-500">
          {currentFilter === 'pending' ? 'All caught up! No pending youth ambassador requests.' : `No ${currentFilter} youth ambassadors found.`}
        </p>
      </div>
    );
  }

  // Render Pending Requests as Cards
  if (currentFilter === 'pending') {
    return (
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {safeRequests.map((request) => (
            <div key={request._id} className="bg-white overflow-hidden shadow-lg rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300">
              {/* Header with Profile Picture */}
              <div className="relative h-24 bg-gradient-to-r from-blue-600 to-purple-600">
                {request.profilePicture && (
                  <div className="absolute -bottom-6 left-6">
                    <img 
                      src={getImageSrc(request.profilePicture)} 
                      alt={`${request.name} profile`}
                      className="w-12 h-12 rounded-full border-4 border-white bg-white object-cover"
                    />
                  </div>
                )}
                {!request.profilePicture && (
                  <div className="absolute -bottom-6 left-6">
                    <div className="w-12 h-12 rounded-full border-4 border-white bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-600 font-medium text-sm">
                        {request.name.charAt(0)}
                      </span>
                    </div>
                  </div>
                )}
                <div className="absolute top-4 right-4">
                  {getStatusBadge(request)}
                </div>
              </div>

              <div className="pt-8 p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">{request.name}</h3>
                  <p className="text-sm text-gray-600 font-medium">{request.instituteName}</p>
                  <p className="text-sm text-gray-500">{request.city}</p>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 w-16">Email:</span>
                    <span className="text-sm text-gray-900 truncate">{request.email}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 w-16">Mobile:</span>
                    <span className="text-sm text-gray-900">{request.mobileNumber}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-xs text-gray-500">
                    Registered {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
                  </span>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleViewDetails(request)}
                    className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => handleApproveClick(request)}
                    className="flex-1 bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleRejectClick(request)}
                    className="flex-1 bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {renderPagination()}
        {renderModal()}
      </div>
    );
  }

  // Render Approved/Rejected/Active/Inactive Students as Table
  return (
    <div className="space-y-6">
      <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={`${
              currentFilter === 'approved' ? 'bg-green-50' : 
              currentFilter === 'rejected' ? 'bg-red-50' :
              currentFilter === 'active' ? 'bg-blue-50' : 'bg-gray-50'
            }`}>
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Institute & Location
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registration Date
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeRequests.map((request) => (
                <tr key={request._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {request.profilePicture ? (
                        <img 
                          src={getImageSrc(request.profilePicture)} 
                          alt={`${request.name} profile`}
                          className="w-10 h-10 rounded-full mr-4 object-cover border-2 border-gray-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full mr-4 bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 text-sm font-medium">
                            {request.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{request.name}</div>
                        <div className="text-sm text-gray-500">Student ID: {request._id.slice(-8)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{request.email}</div>
                    <div className="text-sm text-gray-500">{request.mobileNumber}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{request.instituteName}</div>
                    <div className="text-sm text-gray-500">{request.city}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      {getStatusBadge(request)}
                      {getActivityBadge(request)}
                    </div>
                    {request.rejectionReason && (
                      <div className="text-xs text-red-600 mt-1 max-w-xs truncate">
                        {request.rejectionReason}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{format(new Date(request.createdAt), 'MMM dd, yyyy')}</div>
                    <div className="text-xs">{format(new Date(request.createdAt), 'hh:mm a')}</div>
                    {request.lastLoginAt && (
                      <div className="text-xs text-green-600 mt-1">
                        Last login: {format(new Date(request.lastLoginAt), 'MMM dd')}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() => handleViewDetails(request)}
                        className="text-[#0e3d1f] hover:text-[#1d4000] transition-colors"
                      >
                        View Details
                      </button>
                      {currentFilter !== 'pending' && (
                        <button
                          onClick={() => handleToggleStatusClick(request, !request.isActive)}
                          className={`text-sm ${
                            request.isActive 
                              ? 'text-red-600 hover:text-red-800' 
                              : 'text-blue-600 hover:text-blue-800'
                          } transition-colors`}
                        >
                          {request.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {renderPagination()}
      {renderModal()}
    </div>
  );

  function renderPagination() {
    if (safePagination.total <= 1) return null;

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => onPageChange(safePagination.current - 1)}
            disabled={safePagination.current === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(safePagination.current + 1)}
            disabled={safePagination.current === safePagination.total}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((safePagination.current - 1) * 10) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(safePagination.current * 10, safePagination.totalRecords)}</span> of{' '}
              <span className="font-medium">{safePagination.totalRecords}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => onPageChange(safePagination.current - 1)}
                disabled={safePagination.current === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Previous</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                </svg>
              </button>
              
              {[...Array(Math.min(safePagination.total, 5))].map((_, index) => {
                let page;
                if (safePagination.total <= 5) {
                  page = index + 1;
                } else if (safePagination.current <= 3) {
                  page = index + 1;
                } else if (safePagination.current >= safePagination.total - 2) {
                  page = safePagination.total - 4 + index;
                } else {
                  page = safePagination.current - 2 + index;
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                      page === safePagination.current
                        ? 'z-10 bg-[#0e3d1f] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0e3d1f]'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() => onPageChange(safePagination.current + 1)}
                disabled={safePagination.current === safePagination.total}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="sr-only">Next</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  }

  function renderModal() {
    if (!showModal || !selectedRequest) return null;

    // Details Modal
    if (modalType === 'details') {
      return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-0 border max-w-4xl shadow-lg rounded-lg bg-white">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  {selectedRequest.profilePicture ? (
                    <img 
                      src={getImageSrc(selectedRequest.profilePicture)} 
                      alt={`${selectedRequest.name} profile`}
                      className="w-16 h-16 rounded-full border-4 border-white bg-white object-cover mr-4"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full border-4 border-white bg-gray-200 flex items-center justify-center mr-4">
                      <span className="text-gray-600 font-medium text-xl">
                        {selectedRequest.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-medium text-white">{selectedRequest.name}</h3>
                    <p className="text-blue-100">{selectedRequest.instituteName}</p>
                    <div className="mt-1 flex space-x-2">
                      {getStatusBadge(selectedRequest)}
                      {getActivityBadge(selectedRequest)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Personal Information</h4>
                    <div className="space-y-2">
                      <div><strong>Full Name:</strong> {selectedRequest.name}</div>
                      <div><strong>Email:</strong> {selectedRequest.email}</div>
                      <div><strong>Mobile:</strong> {selectedRequest.mobileNumber}</div>
                      <div><strong>City:</strong> {selectedRequest.city}</div>
                      <div><strong>Student ID:</strong> {selectedRequest._id}</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Academic Information</h4>
                    <div className="space-y-2">
                      <div><strong>Institute:</strong> {selectedRequest.instituteName}</div>
                      {selectedRequest.course && <div><strong>Course:</strong> {selectedRequest.course}</div>}
                      {selectedRequest.yearOfStudy && <div><strong>Year of Study:</strong> {selectedRequest.yearOfStudy}</div>}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Account Information</h4>
                    <div className="space-y-2">
                      <div><strong>Registration Date:</strong> {format(new Date(selectedRequest.createdAt), 'PPpp')}</div>
                      {selectedRequest.lastLoginAt && (
                        <div><strong>Last Login:</strong> {format(new Date(selectedRequest.lastLoginAt), 'PPpp')}</div>
                      )}
                      <div><strong>Login Count:</strong> {selectedRequest.loginCount || 0}</div>
                      <div><strong>Account Status:</strong> {selectedRequest.isActive ? 'Active' : 'Inactive'}</div>
                      <div><strong>Verification Status:</strong> {selectedRequest.isVerified ? 'Verified' : 'Pending'}</div>
                    </div>
                  </div>

                  {selectedRequest.bio && (
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Bio</h4>
                      <p className="text-gray-600">{selectedRequest.bio}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Profile Cover */}
              {selectedRequest.profileCover && (
                <div className="mt-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-3 border-b pb-2">Profile Cover</h4>
                  <img 
                    src={getImageSrc(selectedRequest.profileCover)} 
                    alt={`${selectedRequest.name} cover`}
                    className="w-full h-64 object-cover rounded-lg border"
                  />
                </div>
              )}

              {/* Admin Notes & Rejection Reason */}
              {(selectedRequest.adminNotes || selectedRequest.rejectionReason) && (
                <div className="mt-6 space-y-4">
                  {selectedRequest.rejectionReason && (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="text-lg font-medium text-red-800 mb-2">Rejection Reason</h4>
                      <p className="text-red-700">{selectedRequest.rejectionReason}</p>
                    </div>
                  )}
                  {selectedRequest.adminNotes && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="text-lg font-medium text-blue-800 mb-2">Admin Notes</h4>
                      <p className="text-blue-700">{selectedRequest.adminNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {currentFilter === 'pending' && (
              <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t">
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setModalType('approve');
                      setAdminNotes('');
                    }}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
                  >
                    Approve Student
                  </button>
                  <button
                    onClick={() => {
                      setModalType('reject');
                      setRejectionReason('');
                      setAdminNotes('');
                    }}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors"
                  >
                    Reject Student
                  </button>
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-6 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Action Modals (Approve/Reject/Toggle Status)
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {modalType === 'approve' ? 'Approve Student' : 
               modalType === 'reject' ? 'Reject Student' :
               `${statusAction ? 'Activate' : 'Deactivate'} Student`}
            </h3>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                {selectedRequest.profilePicture ? (
                  <img 
                    src={getImageSrc(selectedRequest.profilePicture)} 
                    alt={`${selectedRequest.name} profile`}
                    className="w-10 h-10 rounded-full border-2 border-gray-200 mr-3 object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full border-2 border-gray-200 mr-3 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-600 font-medium text-sm">
                      {selectedRequest.name.charAt(0)}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedRequest.name}</p>
                  <p className="text-sm text-gray-600">{selectedRequest.email}</p>
                </div>
              </div>
            </div>

            {modalType === 'reject' && (
              <div className="mb-4">
                <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <select
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0e3d1f] focus:border-[#0e3d1f]"
                  required
                >
                  <option value="">Select a reason</option>
                  <option value="Invalid information provided">Invalid information provided</option>
                  <option value="Incomplete registration">Incomplete registration</option>
                  <option value="Duplicate account">Duplicate account</option>
                  <option value="Inappropriate content">Inappropriate content</option>
                  <option value="Institution not recognized">Institution not recognized</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="adminNotes" className="block text-sm font-medium text-gray-700 mb-2">
                Admin Notes (Optional)
              </label>
              <textarea
                id="adminNotes"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0e3d1f] focus:border-[#0e3d1f]"
                placeholder="Add any additional notes..."
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleConfirmAction}
                disabled={processing || (modalType === 'reject' && !rejectionReason)}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  modalType === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' :
                  modalType === 'reject' ? 'bg-red-600 hover:bg-red-700 text-white' :
                  statusAction ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                  'bg-gray-600 hover:bg-gray-700 text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {processing ? 'Processing...' : 
                 modalType === 'approve' ? 'Approve' :
                 modalType === 'reject' ? 'Reject' :
                 statusAction ? 'Activate' : 'Deactivate'
                }
              </button>
              <button
                onClick={() => setShowModal(false)}
                disabled={processing}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default StudentRequestList;